import type { ReactElement, ReactNode } from 'react';
import { Tab, Tabs } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import theme from '../../../theme/theme';

/** One tab entry. Labels are already translated by the caller (this component is i18n-agnostic). */
export interface SubNavItem<V extends string | number = string> {
  /** Unique value identifying the tab; echoed back through `onChange`. */
  value: V;
  /** Visible label. */
  label: ReactNode;
  /** Optional leading icon — omit for tabs that have none. */
  icon?: ReactElement;
  disabled?: boolean;
  /** Accessible name, when `label` is not descriptive on its own. */
  ariaLabel?: string;
}

/** Tab size presets. `medium` matches the main employee-detail tabs. */
export type SubNavBarSize = 'small' | 'medium';

/** Colour overrides; each falls back to the shared main-tab palette. */
export interface SubNavBarColors {
  active?: string;
  inactive?: string;
  hover?: string;
  indicator?: string;
}

export interface SubNavBarProps<V extends string | number = string> {
  items: SubNavItem<V>[];
  /** Currently active tab value. */
  value: V;
  onChange: (value: V) => void;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
  /** Density preset (drives padding + default font size). Defaults to `medium`. */
  size?: SubNavBarSize;
  /** Explicit font-size override (CSS value). Falls back to the `size` preset. */
  fontSize?: number | string;
  /** Colour overrides; defaults keep the shared main-tab palette. */
  colors?: SubNavBarColors;
  /** MUI Tabs layout variant (e.g. `scrollable` for overflow). */
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  /** Merged into the Tabs root sx (escape hatch — rarely needed). */
  sx?: SxProps<Theme>;
}

/**
 * Agnostic tab-style navigation bar, shared by the main employee-detail tabs and
 * the payment sub-navigation so their styling stays consistent by default.
 *
 * Presentational only: it renders `items`, highlights `value`, and reports
 * selections via `onChange`. Icons are optional per item. Density, font size and
 * colours are tunable via `size`/`fontSize`/`colors` (all default to the main-tab
 * look), so a caller that needs a smaller or differently-styled bar configures it
 * through props instead of forking the component. Callers own the surrounding
 * container (sticky bar, border, background…) and translate labels.
 */
export function SubNavBar<V extends string | number = string>({
  items,
  value,
  onChange,
  ariaLabel,
  size = 'medium',
  fontSize,
  colors,
  variant = 'standard',
  sx,
}: SubNavBarProps<V>) {
  const dims = theme.subNav.size[size];
  const resolvedFontSize = fontSize ?? dims.fontSize;
  const active = colors?.active ?? theme.palette.primary.main;
  const inactive = colors?.inactive ?? theme.palette.text.label;
  const hover = colors?.hover ?? theme.palette.text.body;
  const indicator = colors?.indicator ?? theme.palette.primary.main;

  return (
    <Tabs
      value={value}
      onChange={(_, next) => onChange(next as V)}
      aria-label={ariaLabel}
      variant={variant}
      // Array form so a caller's `sx` (object, array, or theme callback — all
      // valid SxProps) merges correctly; spreading `...sx` would break the
      // array/function cases.
      sx={[
        {
          minHeight: dims.minHeight,
          '& .MuiTabs-indicator': {
            backgroundColor: indicator,
            height: theme.subNav.indicatorHeight,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {items.map(item => (
        <Tab
          key={String(item.value)}
          value={item.value}
          disabled={item.disabled}
          label={item.label}
          aria-label={item.ariaLabel}
          {...(item.icon ? { icon: item.icon, iconPosition: 'start' as const } : {})}
          sx={{
            px: dims.px,
            py: dims.py,
            minHeight: dims.minHeight,
            textTransform: 'none',
            fontSize: resolvedFontSize,
            fontWeight: theme.typography.fontWeightMedium,
            color: inactive,
            '&.Mui-selected': { color: active },
            '&:hover': { color: hover },
          }}
        />
      ))}
    </Tabs>
  );
}
